import { type AggRequest, type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { type LoomFolderNode } from './types';

const LOOM_FOLDERS_SEARCH_OPERATION_NAME = 'LoomRovoFoldersSearchQuery';
const loomFoldersSearchGraphQuery = `
	query ${LOOM_FOLDERS_SEARCH_OPERATION_NAME}(
		$siteId: ID!
		$query: String
		$spaceId: ID
		$includeDefaultFolders: Boolean
	) {
		loom_foldersSearch(
			siteId: $siteId
			query: $query
			spaceId: $spaceId
			includeDefaultFolders: $includeDefaultFolders
		) {
			id
			name
			parentSpaceId
		}
	}
`;

export type LoomFoldersSearchQueryVariables = {
	siteId: string;
	query?: string;
	spaceId?: string;
	includeDefaultFolders?: boolean;
	limit?: number;
};

export type LoomFoldersSearchQueryData = {
	loom_foldersSearch: LoomFolderNode[];
};

export type LoomFoldersSearchQueryProps = Pick<
	AggRequest<LoomFoldersSearchQueryVariables>,
	'aggAbsoluteUrl' | 'variables' | 'headers'
>;

const trimToLimit = (
	result: AggResponse<LoomFoldersSearchQueryData>,
	limit?: number,
): AggResponse<LoomFoldersSearchQueryData> =>
	typeof limit === 'number'
		? {
				...result,
				data: result.data
					? {
							...result.data,
							loom_foldersSearch: result.data.loom_foldersSearch.slice(0, limit),
						}
					: result.data,
			}
		: result;

export const loomFoldersSearchQuery = async ({
	variables,
	aggAbsoluteUrl,
	headers,
}: LoomFoldersSearchQueryProps): Promise<AggResponse<LoomFoldersSearchQueryData>> => {
	// `limit` is not sent to AGG: `loom_foldersSearch` has no limit variable in the GraphQL schema,
	// so we request the full result set and cap length client-side in `trimToLimit` below.
	const { siteId, query, spaceId, includeDefaultFolders, limit } = variables;

	const response = await fetchAgg<
		Pick<LoomFoldersSearchQueryVariables, 'siteId' | 'query' | 'spaceId' | 'includeDefaultFolders'>,
		LoomFoldersSearchQueryData
	>({
		variables: {
			siteId,
			query,
			spaceId,
			includeDefaultFolders,
		},
		aggAbsoluteUrl,
		graphQuery: loomFoldersSearchGraphQuery,
		operationName: LOOM_FOLDERS_SEARCH_OPERATION_NAME,
		headers,
	});

	return trimToLimit(response, limit);
};
